const en = {
  statusVoices: "Natural call voices (Voice Lab)",
  statusVoicesHint: "Compare ElevenLabs, Hakim, Gemini, Cartesia and OpenAI voices and choose one per dialect and gender. Without one, calls use browser voices.",
  personaPortraits: "AI call persona portraits (Flux)",
  metaTitle: "AI & credits",
  title: "AI & credits",
  description: "Decide which AI features are free, what they cost in credits, how much each plan includes every month, and which credit packs merchants can buy.",

  // Status
  statusTitle: "Status",
  statusDesc: "Integrations are configured with environment variables on the server. Secret values are never shown here.",
  statusConfigured: "Configured",
  statusMissing: "Not configured",
  statusClaude: "Text AI — OpenAI GPT or Claude (prompts, calls, translation)",
  statusClaudeHint: "Uses OpenAI when an OpenAI key is saved in Voice Lab → Providers (or {vars} is set); otherwise Claude with ANTHROPIC_API_KEY.",
  statusImages: "AI images (Flux)",
  imagesFlux: "Flux on Replicate makes store photos.",
  imagesOpenai: "No Replicate token: OpenAI images are used (with the OpenAI key). Add a Replicate token to use Flux.",
  imagesNone: "Add a Replicate token (Flux) or an OpenAI key in the Voice Lab to create product photos.",
  imageKeyLabel: "Replicate API token",
  imageKeySavedPlaceholder: "Token saved — paste a new one to replace it",
  imageKeySave: "Save token",
  imageKeyRemove: "Remove saved token",
  imageKeyHelp: "Get it at replicate.com → Account → API tokens. Stored encrypted; never shown again.",
  imageTest: "Test image generation",
  imageTestOk: "{provider} works: a test image was created ({kb} KB in {s} s).",
  imageTestFailed: "{provider} failed: {error}",
  imageKeySaved: "Image token saved.",
  imageKeyRemoved: "Image token removed.",
  statusImagesHint: "Set {vars}.",
  statusStripe: "Platform Stripe (credit purchases)",
  statusStripeHint: "Set {vars}. Without it merchants can't buy credits online — you can still grant credits manually.",
  statusWebhookMissing: "The webhook secret is missing: purchases will only be credited when the buyer returns to the site.",

  // Usage
  usageTitle: "Usage & revenue",
  statSold: "Credits sold",
  statRevenue: "Credit revenue",
  statSpent: "Credits spent",
  statOutstanding: "Unused credits",
  statOutstandingSub: "Sum of all balances",
  statPrompts: "AI prompts",
  statMinutes: "Call minutes",
  statImages: "AI images",
  statThisMonth: "This month",
  statAllTime: "All time",

  // Free or paid
  pricingTitle: "Free or paid",
  pricingDesc: "Free features never use credits. Paid features first use the monthly allowance included in the merchant's plan, then charge credits.",
  feature_prompt: "AI prompts",
  feature_call: "Voice calls",
  feature_image: "AI images",
  feature_translation: "AI translation",
  featureDesc_prompt: "Requests to the AI store assistant.",
  featureDesc_call: "Live voice calls with the AI assistant.",
  featureDesc_image: "Generated product and banner images.",
  featureDesc_translation: "Automatic translation of store content.",
  modeFree: "Free for everyone",
  modeMetered: "Paid — plan allowance, then credits",
  cost_prompt: "Credits per request (at Medium)",
  cost_call: "Credits per minute (at Medium)",
  cost_image: "Credits per image",
  cost_translation: "Credits per item",
  costHintLevel: "Multiplied by the level's price multiplier.",
  costHintNoAllowance: "Plans have no free translation allowance: credits are charged from the first item.",
  signupCredits: "Sign-up bonus credits",
  signupCreditsHint: "Granted once to each new account.",
  lowBalance: "Low-balance warning",
  lowBalanceHint: "Merchants are warned when their balance falls below this many credits.",

  // Levels
  levelsTitle: "Model levels",
  levelsDesc: "Each level maps to a model and effort. Use OpenAI models (gpt-5-mini, gpt-5…) or Claude models (claude-haiku-5-5…). If a level's model belongs to the provider that isn't active, that provider's default is used (Low/Medium: gpt-5-mini, High/Max: gpt-5). The price multiplier scales the credit cost of prompts and calls.",
  level_low: "Low",
  level_medium: "Medium",
  level_high: "High",
  level_max: "Max",
  colLevel: "Level",
  colEnabled: "Enabled",
  colModel: "Model",
  colEffort: "Effort",
  colMultiplier: "Price multiplier",
  effort_low: "Low",
  effort_medium: "Medium",
  effort_high: "High",
  defaultLevel: "Default level",
  defaultLevelHint: "Used when a merchant doesn't pick a level. It must be enabled.",
  enableLevel: "Enable {level}",
  saveSettings: "Save AI settings",
  settingsSaved: "AI settings saved.",
  invalidSettings: "The settings couldn't be read. Reload the page and try again.",

  // Plan allowances
  plansTitle: "Plan allowances",
  plansDesc: "How much is free each month, per plan. Paid features use this allowance before charging credits.",
  freeUsers: "Free users",
  inactive: "Inactive",
  freePrompts: "Free prompts / month",
  freeCallMinutes: "Free call minutes / month",
  freeImages: "Free images / month",
  maxLevel: "Highest level",
  savePlan: "Save",
  planSaved: "Allowance saved.",
  noPlans: "No plans yet.",

  // Packs
  packsTitle: "Credit packs",
  packsDesc: "Packs merchants can buy with a card (Stripe Checkout on the platform account).",
  addPack: "Add pack",
  editPack: "Edit",
  editPackTitle: "Edit credit pack",
  newPackTitle: "New credit pack",
  colPack: "Pack",
  colPrice: "Price",
  colCredits: "Credits",
  colBonus: "Bonus",
  colBadge: "Badge",
  colStatus: "Status",
  colOrder: "Order",
  packLabel: "Label",
  packLabelHint: "Optional, e.g. “Starter pack”.",
  packCurrency: "Currency",
  packPrice: "Price",
  packCredits: "Credits",
  packBonus: "Bonus credits",
  packBadge: "Badge",
  badgeNone: "None",
  badge_popular: "Popular",
  badge_best_value: "Best value",
  packActive: "Available for purchase",
  packOrder: "Display order",
  savePack: "Save pack",
  packSaved: "Credit pack saved.",
  noPacks: "No credit packs yet. Add one so merchants can buy credits.",
  active: "Active",
  untitledPack: "{credits} credits",

  // User credits
  userCreditsTitle: "Credits",
  userCreditsDesc: "AI credit wallet for this account.",
  balance: "Balance",
  purchased: "Purchased",
  spent: "Spent",
  granted: "Granted",
  noAccount: "No credit wallet yet — it is created the first time this user uses AI or receives credits.",
  recentLedger: "Recent movements",
  colWhen: "When",
  colKind: "Type",
  colChange: "Change",
  colBalanceAfter: "Balance after",
  colReason: "Reason",
  kind_signup: "Sign-up bonus",
  kind_purchase: "Purchase",
  kind_spend: "Usage",
  kind_refund: "Refund",
  kind_admin: "Admin adjustment",
  noLedger: "No credit movements yet.",
  adjustTitle: "Adjust credits",
  adjustDelta: "Credits",
  adjustDeltaHint: "Positive to add, negative to remove (e.g. 500 or -200).",
  adjustReason: "Reason",
  adjustReasonHint: "Required — recorded in the ledger and the audit log.",
  adjustSubmit: "Apply adjustment",
  adjustDone: "Credits adjusted. New balance: {balance}.",
  adjustInvalid: "Enter a whole number of credits other than zero.",
  adjustReasonRequired: "A reason is required.",
};

export default en;
